import React, { useState } from 'react';
import './StopActionsModal.css';

const StopActionsModal = ({ 
  isOpen, 
  onClose, 
  selectedStop,
  currentLocation,
  currentETA,
  onScheduleBreak,
  onCompleteRoute,
  scheduledCompletionInfo,
  startPointLog,
  breakLogs,
  onSetBreakLogs,
  endPointLog,
  onSetEndPointLog,
  loopedRouteData,
  canChangeStartPoint,
  canDeleteBreaks,
  canDeleteEndPoint,
  isOnBreak,
  onDeleteStartPoint,
  onDeleteBreak,
  onDeleteEndPoint,
  driverData,
  userSettings,
  routeDetails // Add routeDetails to get schedule information
}) => {
  const [breakDuration, setBreakDuration] = useState(5); // Changed default to 5 minutes
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [showConfirmButton, setShowConfirmButton] = useState(false);
  
  // the breaklog has a StopId but not a stop name. match the id int he loopedRouteData to get the name 
  const breakStopName =  (breakLogs.length > 0) ?
    (loopedRouteData.find(stop =>
      (stop.stopId === breakLogs[0].StopId || stop.Id === breakLogs[0].StopId || stop.StopId === breakLogs[0].StopId)
      )?.Name || 'Unknown Stop')
      : 'Unknown Stop';

  
  
  // Calculate departure time based on stop's ETA and break duration
  const calculateDepartureTime = () => {
    if (!selectedStop) return null;
    
    // Get the stop's ETA directly
    const stopETA = selectedStop.arrival || selectedStop.estimatedArrivalTime || selectedStop.eta;
    if (!stopETA) return null;
    
    // Parse the ETA time
    let arrivalDate;
    
    // If it's already a Date object or ISO string
    if (stopETA instanceof Date) {
      arrivalDate = new Date(stopETA);
    } else if (typeof stopETA === 'string' && stopETA.includes('T')) {
      arrivalDate = new Date(stopETA);
    } else if (typeof stopETA === 'string' && stopETA.includes(':')) {
      // If it's in HH:mm format, create a date for today
      const today = new Date();
      const [hours, minutes] = stopETA.split(':').map(num => parseInt(num, 10));
      arrivalDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, 0, 0);
    } else {
      return null;
    }
    
    // Add break duration to get departure time
    const departureDate = new Date(arrivalDate.getTime() + (breakDuration * 60 * 1000));
    
    return departureDate;
  };

  const handleScheduleBreak = async () => {
    if (!selectedStop) {
      alert('No stop selected for break scheduling');
      return;
    }

    setLoading(true);
    setLoadingAction('break');
    
    try {
      const departureDate = calculateDepartureTime();
      if (!departureDate) {
        throw new Error('Unable to calculate departure time');
      }
      
      // Get schedule information from routeDetails or selectedStop
      const scheduleId = selectedStop.ScheduleId || routeDetails?.scheduleId;
      const runId = selectedStop.RunId || routeDetails?.runId;
      const routeId = routeDetails?.routeId;
      
      if (!scheduleId) {
        console.warn('No ScheduleId found, break scheduling may not work properly');
      }

      // Prepare break data with complete log information
      const breakData = {
        stop: {
          ...selectedStop,
          ScheduleId: scheduleId,
          RunId: runId
        },
        breakDuration: breakDuration,
        departureTime: departureDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
        // Complete log information for backend
        logData: {
          routeId: routeId,
          runId: runId,
          scheduleId: scheduleId,
          stopId: selectedStop.stopId || selectedStop.Id || selectedStop.StopId,
          itineraryId: selectedStop.itineraryId || selectedStop.ItineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
          stopName: selectedStop.stopName || selectedStop.Name || selectedStop.StopName || selectedStop.address,
          vehicleId: userSettings?.vehicleId,
          driverId: userSettings?.Id,
          departureTime: departureDate.toISOString(), // ISO format for backend
          PAXBoarded: -2, // Break indicator
          breakDuration: breakDuration
        }
      };

      console.log('Scheduling break with data:', breakData);
      await onScheduleBreak(breakData);

      // Update local break logs for UI
      const newBreakLog = {
        Id: Date.now(), // Temporary ID, will be replaced by backend response
        StopId: selectedStop.stopId || selectedStop.Id || selectedStop.StopId,
        ItineraryId: selectedStop.itineraryId || selectedStop.ItineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
        StopName: selectedStop.stopName || selectedStop.Name || selectedStop.StopName || selectedStop.address,
        BreakDuration: breakDuration,
        DepartureTime: departureDate.toISOString(),
        CreatedOn: new Date().toISOString(),
        UpdatedOn: departureDate.toISOString(), // This is when the break ends
        IsETALog: false,
        PAXBoarded: -2,
        RunId: runId,
        ScheduleId: scheduleId,
        VehicleId: userSettings?.vehicleId,
      };
      
      onSetBreakLogs([...breakLogs, newBreakLog]);
      onClose();
    } catch (error) {
      console.error('Error scheduling break:', error);
      alert('Failed to schedule break. Please try again.');
    } finally {
      setLoading(false);
      setLoadingAction('');
    }
  };

  const handleCompleteRoute = () => {
    if (!selectedStop) {
      alert('No stop selected for route completion');
      return;
    }
    
    // Show the confirmation button
    setShowConfirmButton(true);
  };

  const executeCompleteRoute = async () => {
    if (!selectedStop) {
      alert('No stop selected for route completion');
      return;
    }

    const stopName = selectedStop.stopName || selectedStop.Name || selectedStop.StopName || selectedStop.address;
    
    setLoading(true);
    setLoadingAction('complete');
    
    try {
      // Get schedule information from routeDetails or selectedStop
      const scheduleId = selectedStop.ScheduleId || routeDetails?.scheduleId;
      const runId = selectedStop.RunId || routeDetails?.runId;
      const routeId = routeDetails?.routeId;
      const stopId = selectedStop.stopId || selectedStop.Id || selectedStop.StopId;
      
      if (!scheduleId) {
        console.warn('No ScheduleId found, route completion may not work properly');
      }

      // Get the stop's ETA as the completion time (similar to calculateDepartureTime logic)
      const stopETA = selectedStop.arrival || selectedStop.estimatedArrivalTime || selectedStop.eta;
      let completionDate;
      
      if (stopETA instanceof Date) {
        completionDate = new Date(stopETA);
      } else if (typeof stopETA === 'string' && stopETA.includes('T')) {
        completionDate = new Date(stopETA);
      } else if (typeof stopETA === 'string' && stopETA.includes(':')) {
        // If it's in HH:mm format, create a date for today
        const today = new Date();
        const [hours, minutes] = stopETA.split(':').map(num => parseInt(num, 10));
        completionDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, 0, 0);
      } else {
        // Fallback to current time if ETA cannot be parsed
        console.warn('Could not parse stop ETA, using current time as fallback');
        completionDate = new Date();
      }

      // Prepare completion data with complete log information
      const completionData = {
        stopId: stopId,
        stopName: stopName,
        currentLocation: currentLocation,
        // Complete log information for backend
        logData: {
          routeId: routeId,
          runId: runId,
          scheduleId: scheduleId,
          stopId: stopId,
          itineraryId: selectedStop.itineraryId || selectedStop.ItineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
          stopName: stopName,
          vehicleId: userSettings?.vehicleId,
          driverId: userSettings?.Id,
          completedAt: completionDate.toISOString(),
          PAXBoarded: -3 // Route end indicator
        }
      };

      console.log('Completing route with data:', completionData);
      await onCompleteRoute(completionData);
      
      // Update local end point log for UI
      const newEndPointLog = {
        Id: Date.now(), // Temporary ID, will be replaced by backend response
        StopId: stopId,
        ItineraryId: selectedStop.itineraryId || selectedStop.ItineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
        StopName: stopName,
        CreatedOn: new Date().toISOString(),
        UpdatedOn: completionDate.toISOString(), // Use the same completion time
        IsETALog: false,
        PAXBoarded: -3,
        RunId: runId,
        ScheduleId: scheduleId,
        VehicleId: userSettings?.vehicleId,
      };
      
      onSetEndPointLog(newEndPointLog);
      onClose();
    } catch (error) {
      console.error('Error completing route:', error);
      alert('Failed to mark route as complete. Please try again.');
    } finally {
      setLoading(false);
      setLoadingAction('');
    }
  };

  const handleCancel = () => {
    setBreakDuration(15);
    setShowConfirmButton(false);
    onClose();
  };

  if (!isOpen || !selectedStop) return null;

  // Get formatted departure time for display
  const departureDate = calculateDepartureTime();
  const formattedDepartureTime = departureDate ? 
    departureDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
    null;

  // Determine break state based on loopedRouteData and breakLogs
  const isCurrentlyOnBreak = loopedRouteData && loopedRouteData.length > 0 && loopedRouteData[0]?.breakStep === true;

  // Check if route end is already scheduled for this stop
  const selectedStopId = selectedStop.stopId || selectedStop.Id || selectedStop.StopId;

  // Check if route end is already scheduled for this stop
  const isEndScheduledForThisStop = endPointLog && (
    endPointLog.StopId === selectedStopId || 
    scheduledCompletionInfo?.stopId === selectedStopId ||
    scheduledCompletionInfo?.stopId === selectedStop.Id
  );

  return (
    <div className="stop-actions-modal-overlay">
      <div className="stop-actions-modal">
        <div className="stop-actions-modal-header">
          <h2>Route Actions</h2>
          <div className="stop-info-compact">
            <span className="stop-name">{selectedStop.address || selectedStop.Name}</span>
            <span className="stop-eta">ETA: {
              (() => {
                const etaTime = selectedStop.arrival || selectedStop.estimatedArrivalTime || selectedStop.eta;
                if (!etaTime) return 'N/A';
                
                // Parse the ETA time and format it properly
                const etaDate = new Date(etaTime);
                return isNaN(etaDate.getTime()) ? etaTime : etaDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
              })()
            }</span>
          </div>
          <button className="stop-actions-modal-close" onClick={handleCancel}>×</button>
        </div>
        
        <div className="stop-actions-modal-content">


          {/* Break Sections - Show one of three states */}
          
          {/* TOP DIV: Break scheduled but not currently on break */}
          {breakLogs && breakLogs.length > 0 && !isCurrentlyOnBreak && (
            <div className="action-section breaks-management-section">
              <h3>Scheduled Breaks</h3>
              <div className="compact-form">
                {breakLogs.map((breakLog, index) => (
                  <div key={breakLog.Id || index} >
                    <div className="break-item-content">
                      <div className="break-main-info">
                        <strong>Break at:</strong> {breakStopName || 'Unknown Stop'}
                      </div>
                      <div className="break-time-info">
                        <span className="time-label">Departure:</span>
                        <span className="time-value">
                          {new Date(breakLog.DepartureTime || breakLog.UpdatedOn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                    </div>
                    {canDeleteBreaks && (
                      <div className="break-actions">
                        <button 
                          className="btn-action btn-delete btn-small" 
                          onClick={() => onDeleteBreak({
                            breakLogId: breakLog.Id,
                            stopId: breakLog.StopId || selectedStop?.stopId || selectedStop?.Id || selectedStop?.StopId,
                            itineraryId: breakLog.ItineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
                            runId: breakLog.RunId || routeDetails?.runId,
                            scheduleId: breakLog.ScheduleId || routeDetails?.scheduleId,
                            driverId: userSettings?.Id,
                            vehicleId: userSettings?.vehicleId,
                            routeId: routeDetails?.routeId
                          })}
                          disabled={loading}
                        >
                          {loading && loadingAction === `delete-break-${breakLog.Id}` ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {!canDeleteBreaks && (
                  <div className="warning-compact">⚠️ Cannot delete breaks after end time</div>
                )}
              </div>
            </div>
          )}

          {/* MIDDLE DIV: Currently on break */}
          {isCurrentlyOnBreak && (
            <div className="action-section on-break-section">
              <h3>Currently On Break</h3>
              <div className="compact-form">
                <div className="on-break-info">
                  <p>🛑 You are currently on break</p>
                  <div className="warning-compact">Complete your break to continue</div>
                </div>
              </div>
            </div>
          )}

          {/* BOTTOM DIV: No breaks scheduled - show schedule break form */}
          {(!breakLogs || breakLogs.length === 0) && !isCurrentlyOnBreak && (
            <div className="action-section break-section">
              <h3>Schedule Break</h3>
              <div className="compact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Duration (min):</label>
                    <select
                      value={breakDuration}
                      onChange={(e) => setBreakDuration(parseInt(e.target.value))}
                      disabled={loading}
                      className="compact-select"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={45}>45</option>
                      <option value={60}>60</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Departure:</label>
                    <span className="departure-display">{formattedDepartureTime || 'Calculating...'}</span>
                  </div>
                </div>
                <button 
                  className="btn-action btn-break" 
                  onClick={handleScheduleBreak}
                  disabled={loading || !formattedDepartureTime}
                >
                  {loading && loadingAction === 'break' ? 'Scheduling...' : 'Schedule Break'}
                </button>
              </div>
            </div>
          )}

          {/* End Point Management Section */}
          {endPointLog && (
            <div className="action-section end-point-section">
              <h3>Route End Point</h3>
              <div className="compact-form">
                <div className="end-point-info">
                  <p>✓ Route end scheduled</p>
                  <div className="scheduled-time">
                    Scheduled at: {new Date(endPointLog.CreatedOn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                  {canDeleteEndPoint && (
                    <button 
                      className="btn-action btn-delete" 
                      onClick={onDeleteEndPoint}
                      disabled={loading}
                    >
                      {loading && loadingAction === 'delete-end' ? 'Removing...' : 'Cancel Route End'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Route Completion Section */}
          {!endPointLog && (
          <div className="action-section completion-section">
            <h3>Complete Route</h3>
            <div className="compact-form">
              {isEndScheduledForThisStop ? (
                <div className="completion-scheduled">
                  <p>✓ Route completion already scheduled for this stop</p>
                  <div className="scheduled-time">
                    Scheduled completion time: {scheduledCompletionInfo?.scheduledTime || 'When reached'}
                  </div>
                  <button 
                    className="btn-action btn-complete" 
                    onClick={showConfirmButton ? executeCompleteRoute : handleCompleteRoute}
                    disabled={loading}
                  >
                    {loading && loadingAction === 'complete' ? 'Completing...' : (showConfirmButton ? 'Complete Now' : 'Complete Now')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="completion-info">
                    <p>Mark route complete after this stop</p>
                    <div className="warning-compact">⚠️ You will be directed back to the lot</div>
                  </div>
                  <button 
                    className="btn-action btn-break" 
                    onClick={showConfirmButton ? executeCompleteRoute : handleCompleteRoute}
                    disabled={loading}
                  >
                    {loading && loadingAction === 'complete' ? 'Completing...' : 'Complete Route'}
                  </button>
                  {showConfirmButton && (
                    <button 
                      className="btn-action btn-complete" 
                      onClick={executeCompleteRoute}
                      disabled={loading}
                      style={{ marginTop: '10px', backgroundColor: '#d32f2f' }}
                    >
                      {loading && loadingAction === 'complete' ? 'Confirming...' : 'Confirm Route Completion'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          )}
        </div>
        
        <div className="stop-actions-modal-actions">
          <button 
            className="btn-cancel" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default StopActionsModal;
